import { FREE_SHIPPING_THRESHOLD } from "../constants";
import React, { useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, ListGroup, Image, Form, Button, Card, Alert, ProgressBar, Badge, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";
import { FaTrashAlt } from 'react-icons/fa';
import Message from "../components/Message";
import { addToCart, removeFromCart } from "../slices/cartSlice";
import { calculateDiscountedPrice, getDiscountDisplayPercentage } from "../utils/discountUtils";
import { isEtaPassed } from "../utils/etaUtils";

const CartScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const cart = useSelector((state) => state.cart);
    const { cartItems } = cart;

    const { userInfo } = useSelector((state) => state.auth);

    const [removing, setRemoving] = useState(null);


    const hasPreOrder = cartItems.some(item => item.isPreOrder);
    const totalItems = cartItems.reduce((acc, item) => acc + item.qty, 0);

    const checkoutHandler = () => {
        navigate('/checkout');
    };

    const addToCartHandler = async (product, qty) => {
        if (qty > product.countInStock) {
            alert(`Sorry, only ${product.countInStock} available in stock`);
            return;
        }
        dispatch(addToCart({ ...product, qty }));
    };

    const removeFromCartHandler = (id, size, name) => {
        if (window.confirm(`Remove ${name} from your basket?`)) {
            setRemoving(id);
            dispatch(removeFromCart({ _id: id, size }));
            setTimeout(() => setRemoving(null), 500);
        }
    };

    const subtotal = cartItems.reduce((acc, item) => {
        const discountedPrice = calculateDiscountedPrice(item.price, item.discount ?? 0);
        return acc + item.qty * discountedPrice;
    }, 0);

    const totalSavings = cartItems.reduce((acc, item) => {
        if (item.isClearance) {
            const discount = item.price - calculateDiscountedPrice(item.price, item.discount ?? 0);
            return acc + (discount * item.qty);
        }
        return acc;
    }, 0);

    const shippingProgress = Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
    const amountRemaining = Math.max(FREE_SHIPPING_THRESHOLD - subtotal, 0);

    return (
        <>
            <Link className="btn btn-light my-3 orange-outline" onClick={() => navigate(-1)}>
                Go Back
            </Link>
            <Row>
                <Col md={12} lg={8}>
                    <h1 style={{ marginBottom: '20px' }}>Shopping Basket</h1>
                    {cartItems.length === 0 ? (
                        <Card className="text-center p-5">
                            <Card.Body>
                                <i className="fas fa-shopping-basket fa-3x mb-3 text-muted"></i>
                                <h3>Your basket is empty</h3>
                                <p className="text-muted">Looks like you haven't added anything yet.</p>
                                <Button
                                    variant="primary"
                                    className="blue-outline mt-3"
                                    onClick={() => navigate('/')}
                                >
                                    Start Shopping
                                </Button>
                            </Card.Body>
                        </Card>
                    ) : (
                        <>
                            {/* Free Shipping Progress Bar */}
                            {subtotal < FREE_SHIPPING_THRESHOLD && (
                                <Alert variant="info" className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span>
                                            <i className="fas fa-truck"></i> Add <strong>£{amountRemaining.toFixed(2)}</strong> more for FREE shipping!
                                        </span>
                                        <span className="text-muted">{shippingProgress.toFixed(0)}%</span>
                                    </div>
                                    <ProgressBar
                                        now={shippingProgress}
                                        variant="success"
                                        style={{ height: '8px' }}
                                    />
                                </Alert>
                            )}

                            {subtotal >= FREE_SHIPPING_THRESHOLD && (
                                <Alert variant="success" className="mb-3">
                                    <i className="fas fa-check-circle"></i> You've qualified for <strong>FREE shipping</strong>! (orders of £{FREE_SHIPPING_THRESHOLD} or more)
                                </Alert>
                            )}

                            {hasPreOrder && (
                                <Alert variant="warning" className="mb-3">
                                    <i className="fa-regular fa-box" style={{ marginRight: '8px' }} />
                                    Your basket contains a <strong>pre-order item</strong>. Payment will be authorised but not taken until your item is dispatched.
                                </Alert>
                            )}

                            <ListGroup variant='flush'>
                                <ListGroup.Item className="border-0 d-none d-md-block">
                                    <Row>
                                        <Col md={3} lg={2}><h6></h6></Col>
                                        <Col md={2}><h6>Name</h6></Col>
                                        <Col md={2}><h6>Price</h6></Col>
                                        <Col md={2}><h6>Size</h6></Col>
                                        <Col md={2}><h6>Qty</h6></Col>
                                        <Col md={1}><h6>Remove</h6></Col>
                                    </Row>
                                </ListGroup.Item>

                                {cartItems.map((item) => {
                                    const discountedPrice = calculateDiscountedPrice(item.price, item.discount ?? 0);
                                    return (
                                        <ListGroup.Item key={`${item._id}-${item.size || 'no-size'}`}>
                                            <Row>
                                                <Col xs={6} md={3} lg={2}>
                                                    <Image src={item.image} alt={item.name} fluid rounded />
                                                </Col>
                                                <Col xs={6} md={2}>
                                                    <Link to={`/product/${item.slug || item._id}`}>{item.name}</Link>

                                                    {item.countInStock < 5 && item.countInStock > 0 && (
                                                        <div className="mt-2">
                                                            <Badge bg="warning" text="dark">
                                                                Only {item.countInStock} left!
                                                            </Badge>
                                                        </div>
                                                    )}

                                                    {item.isPreOrder && (
                                                        <div className="mt-2">
                                                            <Badge bg="warning" text="dark">
                                                                <i className="fa-regular fa-box" style={{ marginRight: '4px' }} />
                                                                Pre-Order
                                                            </Badge>
                                                            {item.eta && !isEtaPassed(item.eta) && (
                                                                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                                                                    Expected {new Date(item.eta).toLocaleDateString('en-GB', {
                                                                    day: 'numeric',
                                                                    month: 'long',
                                                                    year: 'numeric'
                                                                })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </Col>
                                                <Col xs={6} md={2}>
                                                    {item.isClearance ? (
                                                        <>
                                                            <span style={{ textDecoration: 'line-through', fontSize: '0.9em', color: '#999' }}>
                                                                £{item.price.toFixed(2)}
                                                            </span>
                                                            <br />
                                                            <span className="text-danger fw-bold">£{discountedPrice.toFixed(2)}</span>
                                                        </>
                                                    ) : (
                                                        <span>£{item.price.toFixed(2)}</span>
                                                    )}
                                                </Col>
                                                {item.size && (
                                                    <Col xs={6} md={2}>
                                                        Size: <strong>{item.size}</strong>
                                                    </Col>
                                                )}
                                                <Col xs={6} md={2}>
                                                    {item.isPreOrder ? (
                                                        <span>1</span>
                                                    ) : (
                                                        <Form.Control
                                                            as='select'
                                                            value={item.qty}
                                                            onChange={(e) => addToCartHandler(item, Number(e.target.value))}
                                                            aria-label={`Quantity for ${item.name}`}
                                                        >
                                                            {[...Array(item.countInStock).keys()].map((x) => (
                                                                <option key={x + 1} value={x + 1}>
                                                                    {x + 1}
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    )}
                                                </Col>
                                                <Col xs={6} md={1}>
                                                    <Button
                                                        type='button'
                                                        variant='light'
                                                        onClick={() => removeFromCartHandler(item._id, item.size, item.name)}
                                                        disabled={removing === item._id}
                                                        aria-label={`Remove ${item.name} from basket`}
                                                    >
                                                        {removing === item._id ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <FaTrashAlt />
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                    );
                                })}
                            </ListGroup>
                        </>
                    )}
                </Col>
                {cartItems.length > 0 && <Col md={6} lg={4}>
                    <ListGroup variant="flush" className="border-0">
                        <ListGroup.Item className="d-md-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mb-0">
                                    Subtotal ({totalItems} item{totalItems === 1 ? '' : 's'})
                                </h3>
                            </div>
                            <div className="mt-2 mt-md-0">
                                <h3 className="mb-0">£{subtotal.toFixed(2)}</h3>
                            </div>
                        </ListGroup.Item>

                        {totalSavings > 0 && (
                            <ListGroup.Item className="bg-success bg-opacity-10 border-0">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-success">
                                        <i className="fas fa-tag"></i> You're saving:
                                    </span>
                                    <strong className="text-success">£{totalSavings.toFixed(2)}</strong>
                                </div>
                            </ListGroup.Item>
                        )}

                        <ListGroup.Item>
                            <Row>
                                <Col xs={12} md={6} className="order-2 order-md-1 mt-2 mt-md-0">
                                    <Button
                                        type='button'
                                        variant='info'
                                        className='btn-block orange-outline'
                                        disabled={cartItems.length === 0}
                                        onClick={() => navigate('/')}
                                        style={{ width: '100%' }}
                                    >
                                        Continue Shopping
                                    </Button>
                                </Col>
                                <Col xs={12} md={6} className="order-1 order-md-2">
                                    <Button
                                        type='button'
                                        variant='primary'
                                        className='btn-block blue-outline'
                                        disabled={cartItems.length === 0}
                                        onClick={checkoutHandler}
                                        style={{ width: '100%' }}
                                    >
                                        {hasPreOrder ? 'Checkout Pre-Order' : 'Go To Checkout'}
                                    </Button>
                                </Col>
                            </Row>
                        </ListGroup.Item>

                        {/* Sign in nudge for guests */}
                        {!userInfo && cartItems.length > 0 && (
                            <ListGroup.Item className="text-center">
                                <p className="text-muted small mb-1">Already have an account?</p>
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => navigate('/login?redirect=/checkout')}
                                >
                                    Sign in to checkout faster
                                </Button>
                            </ListGroup.Item>
                        )}
                    </ListGroup>
                </Col>}
            </Row>
        </>
    );
};

export default CartScreen;